import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Clock, Send, Loader2, AlertTriangle, ExternalLink, Star, BellRing } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Link } from "react-router-dom";

interface NotificationSetting {
  id: string;
  setting_key: string;
  is_enabled: boolean;
  send_time: string;
}

interface EmailAutoSettingsProps {
  establishmentId?: string;
  type: 'reminder' | 'followup';
}

export function EmailAutoSettings({ establishmentId, type }: EmailAutoSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState<NotificationSetting | null>(null);
  const [reviewLinkConfigured, setReviewLinkConfigured] = useState(false);
  const [preferredPlatformName, setPreferredPlatformName] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    isEnabled: false,
    sendTime: '10:00'
  });

  const settingKey = type === 'reminder' ? 'reservation_reminder' : 'reservation_followup';
  const isFollowup = type === 'followup';

  useEffect(() => {
    fetchSettings();
  }, [establishmentId, type]);

  const fetchSettings = async () => {
    try {
      let query = supabase
        .from('notification_settings')
        .select('*')
        .eq('setting_key', settingKey);

      if (establishmentId) {
        query = query.eq('establishment_id', establishmentId);
      }

      const { data, error } = await query.single();

      if (!error && data) {
        setSettings(data);
        setFormData({
          isEnabled: data.is_enabled,
          sendTime: data.send_time ? `${data.send_time.substring(0, 2)}:00` : '10:00'
        });
      }

      // Check if review link is configured (only for followup)
      if (isFollowup && establishmentId) {
        const { data: estData } = await supabase
          .from('establishments')
          .select('google_business_url, tripadvisor_review_url, yelp_review_url, facebook_review_url, trustpilot_review_url, thefork_review_url, preferred_review_platform')
          .eq('id', establishmentId)
          .single();

        if (estData) {
          const platformNames: Record<string, string> = {
            google: 'Google',
            tripadvisor: 'TripAdvisor',
            yelp: 'Yelp',
            facebook: 'Facebook',
            trustpilot: 'Trustpilot',
            thefork: 'TheFork'
          };
          
          const urlMap: Record<string, string | null> = {
            google: estData.google_business_url,
            tripadvisor: estData.tripadvisor_review_url,
            yelp: estData.yelp_review_url,
            facebook: estData.facebook_review_url,
            trustpilot: estData.trustpilot_review_url,
            thefork: estData.thefork_review_url
          };
          
          const preferredPlatform = estData.preferred_review_platform || 'google';
          const reviewUrl = urlMap[preferredPlatform];
          
          setReviewLinkConfigured(!!reviewUrl);
          setPreferredPlatformName(reviewUrl ? platformNames[preferredPlatform] : null);
        }
      }
    } catch (error) {
      console.error('Erreur chargement settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (dataToSave?: typeof formData) => {
    if (!settings) return;
    
    const data = dataToSave || formData;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('notification_settings')
        .update({
          is_enabled: data.isEnabled,
          send_time: data.sendTime + ':00'
        })
        .eq('id', settings.id)
        .select();

      if (error) throw error;

      toast({
        title: "Paramètres enregistrés",
        description: "Les paramètres ont été mis à jour."
      });
      
      fetchSettings();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les paramètres.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = (checked: boolean) => {
    if (isFollowup && !reviewLinkConfigured) {
      toast({
        title: "Configuration requise",
        description: "Veuillez d'abord configurer un lien d'avis dans les paramètres de l'établissement.",
        variant: "destructive"
      });
      return;
    }
    const newFormData = { ...formData, isEnabled: checked };
    setFormData(newFormData);
    handleSave(newFormData);
  };

  const handleTestEmail = async () => {
    if (isFollowup && !reviewLinkConfigured) {
      toast({
        title: "Configuration requise",
        description: "Veuillez d'abord configurer un lien d'avis dans les paramètres de l'établissement.",
        variant: "destructive"
      });
      return;
    }
    
    setTesting(true);
    try {
      const functionName = isFollowup ? 'send-reservation-followup' : 'send-reservation-reminders';
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: { test: true, establishmentId }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Test envoyé",
          description: data.message || (isFollowup ? "Les emails de suivi test ont été envoyés." : "Les rappels test ont été envoyés.")
        });
      } else {
        toast({
          title: "Information",
          description: data.message || (isFollowup ? "Aucune réservation arrivée hier." : "Aucune réservation demain.")
        });
      }
    } catch (error: any) {
      console.error('Erreur test:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible d'envoyer le test.",
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const Icon = isFollowup ? Star : BellRing;
  const title = isFollowup ? "Configuration envoi automatique" : "Configuration envoi automatique";
  const description = isFollowup 
    ? "Configurez l'envoi automatique des emails de suivi aux clients le lendemain de leur visite."
    : "Configurez l'envoi automatique des rappels aux clients la veille de leur réservation.";

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Alerte si aucun lien d'avis configuré (uniquement pour followup) */}
        {isFollowup && !reviewLinkConfigured && (
          <Alert variant="destructive" className="bg-orange-500/10 border-orange-500/50">
            <AlertTriangle className="h-4 w-4 text-orange-500" />
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center gap-2">
              <span className="text-orange-700 dark:text-orange-300">
                Aucun lien d'avis configuré. Configurez d'abord un lien dans les paramètres de l'établissement.
              </span>
              <Link to="/establishment" className="inline-flex items-center gap-1 text-primary hover:underline font-medium">
                <ExternalLink className="w-3 h-3" />
                Configurer
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Plateforme configurée */}
        {isFollowup && reviewLinkConfigured && preferredPlatformName && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
            <Star className="w-4 h-4 text-green-600" />
            <span className="text-sm text-green-700 dark:text-green-300">
              Plateforme configurée : <strong>{preferredPlatformName}</strong>
            </span>
          </div>
        )}

        {/* Toggle activation */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor={`${type}-enabled`}>Activer l'envoi automatique</Label>
            <p className="text-sm text-muted-foreground">
              {isFollowup 
                ? "Un email sera envoyé aux clients marqués comme \"arrivés\" le lendemain de leur visite."
                : "Un email de rappel sera envoyé à chaque client la veille de sa réservation."}
            </p>
          </div>
          <Switch
            id={`${type}-enabled`}
            checked={formData.isEnabled}
            onCheckedChange={handleToggleEnabled}
            disabled={!settings || (isFollowup && !reviewLinkConfigured)}
          />
        </div>

        {/* Heure d'envoi */}
        <div className="space-y-2">
          <Label htmlFor={`${type}-time`} className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Heure d'envoi
          </Label>
          <Input
            id={`${type}-time`}
            type="time"
            step={3600}
            value={formData.sendTime}
            onChange={(e) => {
              const raw = e.target.value;
              const normalized = raw ? `${raw.substring(0, 2)}:00` : raw;
              setFormData(prev => ({ ...prev, sendTime: normalized }));
            }}
            className="w-32"
            disabled={!settings}
          />
          <p className="text-xs text-muted-foreground">
            Les emails partent à l'heure pile choisie, chaque jour (heure suisse).
          </p>

        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
          <Button onClick={() => handleSave()} disabled={saving || !settings}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              "Enregistrer"
            )}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleTestEmail}
            disabled={testing || !settings || (isFollowup && !reviewLinkConfigured)}
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Tester l'envoi
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
